import { Document, Link, Page, Text, View } from "@react-pdf/renderer";
import objectHash from "object-hash";

import {
  isSerializedChecklistItem,
  isSerializedPageBreak,
  SerializedChecklist,
  SerializedChecklistElement,
  SerializedChecklistItem,
  SerializedChecklistDocumentElement,
  SerializedChecklistHeader,
  isSerializedChecklistHeader,
  SerializedChecklistContents,
} from "../../../lib/model/checklistSerialized";
import { ChecklistDocumentOptions } from "../../../lib/model/documentOptions";
import { ChecklistDocumentStyles } from "../../../lib/model/documentStyles";
import { DocumentLayout } from "../../../lib/model/layout";

export const ChecklistDocumentPDF = ({
  checklistDocument,
  checklistStyles,
  layout,
  options,
}: {
  checklistDocument: SerializedChecklistContents;
  checklistStyles: ChecklistDocumentStyles;
  layout: DocumentLayout;
  options: ChecklistDocumentOptions;
}): JSX.Element => {
  switch (layout) {
    case "twoColumn":
      return (
        <TwoColumnChecklistDocumentPDF
          checklistDocument={checklistDocument}
          checklistStyles={checklistStyles}
          options={options}
        />
      );
    case "oneColumn":
      return (
        <OneColumnChecklistDocumentPDF
          checklistDocument={checklistDocument}
          checklistStyles={checklistStyles}
          options={options}
        />
      );
  }
  const _exhaustiveCheck: never = layout;
  return layout;
};

export const TwoColumnChecklistDocumentPDF = ({
  checklistDocument,
  checklistStyles,
  options,
}: {
  checklistDocument: SerializedChecklistContents;
  checklistStyles: ChecklistDocumentStyles;
  options: ChecklistDocumentOptions;
}): JSX.Element => {
  const checklistPages = partitionPages(checklistDocument.checklists);

  return (
    <Document
      title={checklistDocument.title}
      creator="fs-checklist-studio"
      producer="fs-checklist-studio"
    >
      {checklistPages.map((checklistPage) => (
        <Page
          size="A4"
          orientation="portrait"
          style={checklistStyles.checklistDocument}
          key={objectHash(checklistPage)}
        >
          {!!checklistDocument.title && (
            <Text style={checklistStyles.checklistDocumentTitle}>{checklistDocument.title}</Text>
          )}
          <View
            style={{
              ...checklistStyles.checklistDocumentBody,
            }}
            wrap={false}
          >
            {checklistPage.map((checklist) => (
              <TwoColumnChecklistPDF
                key={objectHash(checklist)}
                checklist={checklist}
                styles={checklistStyles}
                options={options}
              />
            ))}
            <View
              style={{
                position: "absolute",
                bottom: "5pt",
                left: 0,
                right: 0,
                textAlign: "center",
                display: "flex",
              }}
              fixed
            >
              {options.includeSimOnlyWarning && (
                <Text style={checklistStyles.simOnlyWarning}>
                  For flight simulation only -- not for real-world use
                </Text>
              )}
              {options.includeAttributionInFooter && (
                <Text style={checklistStyles.attributionText}>
                  Created with <Link src="https://fs-checklist.studio">fs-checklist.studio</Link>
                </Text>
              )}
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export const OneColumnChecklistDocumentPDF = ({
  checklistDocument,
  checklistStyles,
  options,
}: {
  checklistDocument: SerializedChecklistContents;
  checklistStyles: ChecklistDocumentStyles;
  options: ChecklistDocumentOptions;
}): JSX.Element => {
  const checklistPages = partitionPages(checklistDocument.checklists);

  return (
    <Document
      title={checklistDocument.title}
      creator="fs-checklist-studio"
      producer="fs-checklist-studio"
    >
      {checklistPages.map((checklistPage) => (
        <Page
          size="A4"
          orientation="portrait"
          style={checklistStyles.checklistDocument}
          key={objectHash(checklistPage)}
        >
          {!!checklistDocument.title && (
            <Text fixed style={checklistStyles.checklistDocumentTitle}>
              {checklistDocument.title}
            </Text>
          )}
          <View style={checklistStyles.checklistDocumentBody} wrap={true}>
            {checklistPage.map((checklist) => (
              <OneColumnChecklistPDF
                key={objectHash(checklist)}
                checklist={checklist}
                styles={checklistStyles}
                options={options}
              />
            ))}
          </View>
          <View fixed style={{ textAlign: "center" }}>
            {options.includeSimOnlyWarning && (
              <Text style={checklistStyles.simOnlyWarning}>
                For flight simulation only -- not for real-world use
              </Text>
            )}
            {options.includeAttributionInFooter && (
              <Text style={checklistStyles.attributionText}>
                Created with <Link src="https://fs-checklist.studio">fs-checklist.studio</Link>
              </Text>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

const TwoColumnChecklistPDF = ({
  checklist,
  styles,
  options,
}: {
  checklist: SerializedChecklist;
  styles: ChecklistDocumentStyles;
  options: ChecklistDocumentOptions;
}): JSX.Element => {
  return (
    <View style={{ ...styles.checklist, width: "49%" }} wrap={false} minPresenceAhead={5}>
      <Text style={styles.checklistTitle}>{checklist.title}</Text>
      <View style={styles.checklistBody}>
        {checklist.content.map((checklistElement: SerializedChecklistElement, index) => {
          if (isSerializedChecklistItem(checklistElement)) {
            return (
              <ChecklistItemPDF
                key={`${objectHash(checklistElement)}-${index}`}
                checklistItem={checklistElement}
                styles={styles}
                options={options}
              />
            );
          } else if (isSerializedChecklistHeader(checklistElement)) {
            return (
              <ChecklistHeaderPDF
                key={`${objectHash(checklistElement)}-${index}`}
                checklistHeader={checklistElement}
                styles={styles}
              />
            );
          } else {
            const _exhaustiveCheck: never = checklistElement;
            return _exhaustiveCheck;
          }
        })}
      </View>
    </View>
  );
};

const OneColumnChecklistPDF = ({
  checklist,
  styles,
  options,
}: {
  checklist: SerializedChecklist;
  styles: ChecklistDocumentStyles;
  options: ChecklistDocumentOptions;
}): JSX.Element => {
  return (
    <View style={{ ...styles.checklist, width: "100%" }} wrap={false} minPresenceAhead={5}>
      <Text style={styles.checklistTitle}>{checklist.title}</Text>
      <View style={styles.checklistBody}>
        {checklist.content.map((checklistElement: SerializedChecklistElement, index) => {
          if (isSerializedChecklistItem(checklistElement)) {
            return (
              <ChecklistItemPDF
                key={`${objectHash(checklistElement)}-${index}`}
                checklistItem={checklistElement}
                styles={styles}
                options={options}
              />
            );
          } else if (isSerializedChecklistHeader(checklistElement)) {
            return (
              <ChecklistHeaderPDF
                key={`${objectHash(checklistElement)}-${index}`}
                checklistHeader={checklistElement}
                styles={styles}
              />
            );
          } else {
            const _exhaustiveCheck: never = checklistElement;
            return _exhaustiveCheck;
          }
        })}
      </View>
    </View>
  );
};

const ChecklistHeaderPDF = ({
  checklistHeader,
  styles,
}: {
  checklistHeader: SerializedChecklistHeader;
  styles: ChecklistDocumentStyles;
}): JSX.Element => {
  const assembledStyles = { ...styles.checklistHeader, ...checklistHeader.styles };
  return (
    <View style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Text style={assembledStyles}>{checklistHeader.header}</Text>
    </View>
  );
};

const ChecklistItemPDF = ({
  checklistItem,
  styles,
  options,
}: {
  checklistItem: SerializedChecklistItem;
  styles: ChecklistDocumentStyles;
  options: ChecklistDocumentOptions;
}) => {
  const autoCapitalize = options.autoCapitalize;

  const itemContent =
    autoCapitalize === "both" || autoCapitalize === "checklist-item"
      ? checklistItem.item.toUpperCase()
      : checklistItem.item;
  const itemSetting =
    autoCapitalize === "both" || autoCapitalize === "checklist-setting"
      ? checklistItem.setting.toUpperCase()
      : checklistItem.setting;

  const assembledStyles = { ...styles.checklistItem, ...checklistItem.styles };

  return (
    <View style={assembledStyles}>
      <Text style={styles.checklistItemLabel}>{itemContent}</Text>
      <Text style={styles.checklistItemSeparatorText}>{".".repeat(1000)}</Text>
      <Text style={styles.checklistItemSetting}>{itemSetting}</Text>
    </View>
  );
};

function partitionPages(
  checklistDocumentElements: SerializedChecklistDocumentElement[]
): SerializedChecklist[][] {
  const partitions: SerializedChecklist[][] = [[]];

  checklistDocumentElements.forEach((checklistDocumentElement) => {
    if (isSerializedPageBreak(checklistDocumentElement)) {
      partitions.push([]);
    } else {
      partitions.at(-1)?.push(checklistDocumentElement);
    }
  });

  return partitions;
}
